import { useState, useEffect } from "react";
import UploadField from "@/components/base/UploadField";
import { deleteFileFromVps } from "@/lib/vpsStorage";

/* ─── Types ──────────────────────────────────────────────────── */
export type MediaPackage = {
  id: string;
  tenant_id: string;
  job_id: string;
  client_id: string;
  package_type: string;
  title: string;
  cover_url: string | null;
  frame_url: string | null;
  video_url: string | null;
  download_url: string | null;
  usb_label: string | null;
  notes: string | null;
  is_published: boolean;
  public_token: string;
  created_at: string;
  updated_at: string;
};

export type SimpleClient = { id: string; full_name: string };
export type SimpleJob    = { id: string; title: string; client_id: string };

export const PACKAGE_TYPES = [
  { key: "online_dvd",   label: "Online DVD",     icon: "ri-disc-line",        desc: "Streaming/downloadable video product" },
  { key: "usb",          label: "USB Package",    icon: "ri-usb-line",         desc: "Physical USB with digital files" },
  { key: "embed",        label: "Embed Player",   icon: "ri-play-circle-line", desc: "Embeddable video player page" },
  { key: "cover_frame",  label: "Cover / Frame",  icon: "ri-image-edit-line",  desc: "Printed cover or frame preview" },
  { key: "combo",        label: "Combo Pack",     icon: "ri-gift-2-line",      desc: "Multiple formats combined" },
];

function generateToken(): string {
  const chars = "abcdefghijklmnopqrstuvwxyz0123456789";
  return Array.from({ length: 32 }, () => chars[Math.floor(Math.random() * chars.length)]).join("");
}

/* ─── Props ──────────────────────────────────────────────────── */
interface Props {
  pkg: MediaPackage | null;
  isNew: boolean;
  clients: SimpleClient[];
  jobs: SimpleJob[];
  tenantId: string;
  onClose: () => void;
  onSave: (data: MediaPackage, uploadMeta?: Record<string, { fileId?: string }>) => void;
}

export default function MediaPackageModal({ pkg, isNew, clients, jobs, tenantId, onClose, onSave }: Props) {
  const blank: MediaPackage = {
    id: "", tenant_id: "", job_id: "", client_id: "",
    package_type: "online_dvd", title: "",
    cover_url: null, frame_url: null, video_url: null,
    download_url: null, usb_label: null, notes: null,
    is_published: false, public_token: generateToken(),
    created_at: "", updated_at: "",
  };

  const [form, setForm]             = useState<MediaPackage>(pkg ?? blank);
  const [filteredJobs, setFilteredJobs] = useState<SimpleJob[]>(jobs);
  const [uploadMeta, setUploadMeta] = useState<Record<string, { fileId?: string }>>();

  useEffect(() => {
    setForm(pkg ?? blank);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pkg, isNew]);

  useEffect(() => {
    if (form.client_id) {
      setFilteredJobs(jobs.filter((j) => j.client_id === form.client_id));
    } else {
      setFilteredJobs(jobs);
    }
  }, [form.client_id, jobs]);

  const set = <K extends keyof MediaPackage>(k: K, v: MediaPackage[K]) =>
    setForm((p) => ({ ...p, [k]: v }));

  const isValid = form.job_id && form.client_id && form.title.trim() && form.package_type;
  const selectedType = PACKAGE_TYPES.find((t) => t.key === form.package_type);

  const showVideo    = ["online_dvd", "embed", "combo"].includes(form.package_type);
  const showDownload = ["online_dvd", "usb", "combo"].includes(form.package_type);
  const showFrame    = ["cover_frame", "combo"].includes(form.package_type);
  const showUsb      = ["usb", "combo"].includes(form.package_type);

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 px-4">
      <div className="bg-white border border-slate-200 rounded-2xl w-full max-w-2xl overflow-hidden max-h-[90vh] flex flex-col shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 shrink-0">
          <div>
            <h2 className="text-[#0f172a] font-semibold text-base">
              {isNew ? "New Media Package" : "Edit Media Package"}
            </h2>
            <p className="text-slate-400 text-xs mt-0.5">
              {selectedType?.desc ?? "Digital media delivery product"}
            </p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-slate-300 hover:text-slate-500 hover:bg-slate-100 rounded-lg transition-all cursor-pointer">
            <span className="w-5 h-5 flex items-center justify-center"><i className="ri-close-line"></i></span>
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-5">
          {/* Package type selector */}
          <div>
            <label className="block text-slate-400 text-xs uppercase tracking-wider mb-2">Package Type</label>
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
              {PACKAGE_TYPES.map((t) => (
                <button
                  key={t.key}
                  onClick={() => set("package_type", t.key)}
                  className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border text-center transition-all cursor-pointer ${
                    form.package_type === t.key
                      ? "border-[#C8A96E] bg-[#C8A96E]/8 text-[#a6853e]"
                      : "border-slate-200 text-slate-400 hover:border-slate-300 hover:text-slate-500"
                  }`}
                >
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className={`${t.icon} text-lg`}></i>
                  </span>
                  <span className="text-[10px] font-medium leading-tight">{t.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Client + Job */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-slate-400 text-xs uppercase tracking-wider mb-1.5">
                Client <span className="text-red-400">*</span>
              </label>
              <select
                value={form.client_id}
                onChange={(e) => { set("client_id", e.target.value); set("job_id", ""); }}
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-[#0f172a] outline-none focus:border-[#C8A96E]/50 appearance-none cursor-pointer"
              >
                <option value="">Select client...</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.full_name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-slate-400 text-xs uppercase tracking-wider mb-1.5">
                Job <span className="text-red-400">*</span>
              </label>
              <select
                value={form.job_id}
                onChange={(e) => set("job_id", e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-[#0f172a] outline-none focus:border-[#C8A96E]/50 appearance-none cursor-pointer"
              >
                <option value="">Select job...</option>
                {filteredJobs.map((j) => <option key={j.id} value={j.id}>{j.title}</option>)}
              </select>
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="block text-slate-400 text-xs uppercase tracking-wider mb-1.5">
              Title <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => set("title", e.target.value)}
              placeholder="Wedding Film 2024 — Premium Package"
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-[#0f172a] placeholder-slate-300 outline-none focus:border-[#C8A96E]/50 transition-colors"
            />
          </div>

          {/* Cover Image — always visible */}
          <UploadField
            label="Cover Image"
            value={form.cover_url ?? ""}
            onChange={(url) => set("cover_url", url || null)}
            accept="image/*"
            hint="JPG, PNG or WEBP — displayed on portal"
            urlPlaceholder="https://..."
            showPreview
            tenantId={tenantId}
            entityType="media_package"
            entityId={pkg?.id || ""}
            folder="media"
            maxSizeMB={20}
            allowedTypes={["image/jpeg", "image/png", "image/webp"]}
            onUploaded={(_url, meta) =>
              setUploadMeta((prev) => ({ ...prev, cover_url: { fileId: meta.fileId } }))
            }
          />

          {/* Frame URL */}
          {showFrame && (
            <UploadField
              label="Frame / Portada Preview"
              value={form.frame_url ?? ""}
              onChange={(url) => set("frame_url", url || null)}
              accept="image/*"
              hint="High-res frame or cover image"
              urlPlaceholder="https://..."
              showPreview
              tenantId={tenantId}
              entityType="media_package"
              entityId={pkg?.id || ""}
              folder="media"
              maxSizeMB={20}
              allowedTypes={["image/jpeg", "image/png", "image/webp"]}
              onUploaded={(_url, meta) =>
                setUploadMeta((prev) => ({ ...prev, frame_url: { fileId: meta.fileId } }))
              }
            />
          )}

          {/* Video URL */}
          {showVideo && (
            <UploadField
              label="Video"
              value={form.video_url ?? ""}
              onChange={(url) => set("video_url", url || null)}
              accept="video/*"
              hint="YouTube, Vimeo, or direct MP4 link"
              urlPlaceholder="https://vimeo.com/... or https://youtu.be/..."
              tenantId={tenantId}
              entityType="media_package"
              entityId={pkg?.id || ""}
              folder="media"
              maxSizeMB={2000}
              onUploaded={(_url, meta) =>
                setUploadMeta((prev) => ({ ...prev, video_url: { fileId: meta.fileId } }))
              }
            />
          )}

          {/* Download URL */}
          {showDownload && (
            <UploadField
              label="Download File"
              value={form.download_url ?? ""}
              onChange={(url) => set("download_url", url || null)}
              accept="*/*"
              hint="ZIP, folder, or cloud drive link"
              urlPlaceholder="https://drive.google.com/... or https://..."
              tenantId={tenantId}
              entityType="media_package"
              entityId={pkg?.id || ""}
              folder="media"
              maxSizeMB={2000}
              onUploaded={(_url, meta) =>
                setUploadMeta((prev) => ({ ...prev, download_url: { fileId: meta.fileId } }))
              }
            />
          )}

          {/* USB Label */}
          {showUsb && (
            <div>
              <label className="block text-slate-400 text-xs uppercase tracking-wider mb-1.5">
                USB Label / Name
              </label>
              <input
                type="text"
                value={form.usb_label ?? ""}
                onChange={(e) => set("usb_label", e.target.value || null)}
                placeholder="USB Premium — Ana & Carlos"
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-[#0f172a] placeholder-slate-300 outline-none focus:border-[#C8A96E]/50 transition-colors"
              />
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="block text-slate-400 text-xs uppercase tracking-wider mb-1.5">
              Notes <span className="text-slate-300">(internal)</span>
            </label>
            <textarea
              value={form.notes ?? ""}
              onChange={(e) => set("notes", e.target.value || null)}
              rows={2}
              placeholder="Internal notes about this package..."
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-[#0f172a] placeholder-slate-300 outline-none focus:border-[#C8A96E]/50 resize-none"
            />
          </div>

          {/* Public token preview */}
          <div className="bg-slate-50 border border-slate-100 rounded-xl p-3">
            <p className="text-slate-400 text-xs mb-1">Public URL:</p>
            <p className="text-slate-500 text-xs font-mono truncate">/media/{form.public_token}</p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 shrink-0 flex items-center justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-500 hover:text-[#0f172a] border border-slate-200 rounded-lg transition-colors cursor-pointer whitespace-nowrap">
            Cancel
          </button>
          <button
            onClick={() => isValid && onSave(form, uploadMeta)}
            disabled={!isValid}
            className="flex items-center gap-2 bg-[#C8A96E] hover:bg-[#b8965a] disabled:opacity-40 disabled:cursor-not-allowed text-black text-sm font-semibold px-5 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-save-line text-sm"></i></span>
            {isNew ? "Create Package" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}